import React from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import RemoveIcon from '@mui/icons-material/Remove';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

const formatUsageLabel = (status) => {
  if (status === 'never_used') return 'Never Used';
  if (status === 'using') return 'Using';
  return '-';
};

const usageColor = (status) => {
  if (status === 'never_used') return 'error.main';
  if (status === 'using') return 'success.main';
  return 'text.secondary';
};

const getOverallProgress = (modules) => {
  let improvedCount = 0;
  let degradedCount = 0;
  modules.forEach((mod) => {
    if (mod.changed) {
      if (mod.improved) improvedCount++;
      else degradedCount++;
    }
  });
  if (improvedCount > degradedCount) return 'up';
  if (degradedCount > improvedCount) return 'down';
  return 'neutral';
};

function SyncProgressReport(props) {
  const [syncDates, setSyncDates] = React.useState([]);
  const [selectedDate1, setSelectedDate1] = React.useState('');
  const [selectedDate2, setSelectedDate2] = React.useState('');
  const [schoolFilter, setSchoolFilter] = React.useState('');
  const [schoolSearchText, setSchoolSearchText] = React.useState('');
  const [schoolOptions, setSchoolOptions] = React.useState([]);
  const [schoolOptionsLoading, setSchoolOptionsLoading] = React.useState(false);
  const [compareData, setCompareData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [datesLoading, setDatesLoading] = React.useState(false);
  const [coordinatorFilter, setCoordinatorFilter] = React.useState('');
  const [supervisorFilter, setSupervisorFilter] = React.useState('');
  const [coordinatorOptions, setCoordinatorOptions] = React.useState([]);
  const [supervisorOptions, setSupervisorOptions] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const tableSectionRef = React.useRef(null);
  const tableContainerRef = React.useRef(null);

  React.useEffect(() => {
    getRequest(GET_URL.client_track_report.api, { coordinator_options: 1 }, props).then((response) => {
      if (response?.status === 200) setCoordinatorOptions(response.data?.data || []);
    }).catch(() => {});
    getRequest(GET_URL.client_track_report.api, { supervisor_options: 1 }, props).then((response) => {
      if (response?.status === 200) setSupervisorOptions(response.data?.data || []);
    }).catch(() => {});
  }, [props]);

  React.useEffect(() => {
    setDatesLoading(true);
    getRequest(
      GET_URL.client_track_report.api,
      { sync_dates: 1 },
      props
    )
      .then((response) => {
        setDatesLoading(false);
        if (response?.status === 200) {
          const dates = response.data?.data || [];
          setSyncDates(dates);
          if (dates.length >= 2) {
            setSelectedDate1(dates[1].date);
            setSelectedDate2(dates[0].date);
          } else if (dates.length === 1) {
            setSelectedDate1(dates[0].date);
          }
        }
      })
      .catch(() => setDatesLoading(false));
  }, [props]);

  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      { institute_options: 1, ...(schoolSearchText.trim() && { search: schoolSearchText.trim() }) },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setSchoolOptions(response.data?.data || []);
      }
    }).catch(() => {});
  }, [props, schoolSearchText]);

  React.useEffect(() => {
    if (!selectedDate1 || !selectedDate2 || selectedDate1 === selectedDate2) {
      setCompareData(null);
      return;
    }
    setLoading(true);
    getRequest(
      GET_URL.client_track_report.api,
      {
        sync_compare: 1,
        sync_date1: selectedDate1,
        sync_date2: selectedDate2,
        ...(schoolFilter.trim() && { school: schoolFilter.trim() }),
        ...(coordinatorFilter && { coordinator: coordinatorFilter }),
        ...(supervisorFilter && { supervisor: supervisorFilter }),
      },
      props
    )
      .then((response) => {
        setLoading(false);
        if (response?.status === 200) {
          setCompareData(response.data?.data || null);
          setPage(0);
        }
      })
      .catch(() => setLoading(false));
  }, [selectedDate1, selectedDate2, schoolFilter, coordinatorFilter, supervisorFilter, props]);

  const summary = compareData?.summary || {};
  const rows = compareData?.rows || [];

  const sortedRows = React.useMemo(() => {
    return [...rows].sort((a, b) => {
      const progA = getOverallProgress(a.modules);
      const progB = getOverallProgress(b.modules);
      
      const score = { up: 2, down: 1, neutral: 0 };
      if (score[progA] !== score[progB]) {
        return score[progB] - score[progA]; // Descending: Up, then Down, then Neutral
      }
      return a.institute_name.localeCompare(b.institute_name);
    });
  }, [rows]);

  const date1Label = React.useMemo(() => {
    const entry = syncDates.find((d) => d.date === selectedDate1);
    return entry?.label || selectedDate1;
  }, [syncDates, selectedDate1]);

  const date2Label = React.useMemo(() => {
    const entry = syncDates.find((d) => d.date === selectedDate2);
    return entry?.label || selectedDate2;
  }, [syncDates, selectedDate2]);

  return (
    <Box p={2}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <CompareArrowsIcon sx={{ fontSize: 32, color: '#4680FF' }} />
        <Box>
          <Typography variant="h6" fontWeight={700}>Sync Progress Report</Typography>
          <Typography variant="body2" color="text.secondary">
            Compare two sync dates to track module adoption progress across all synced data.
          </Typography>
        </Box>
      </Stack>

      {datesLoading && <LinearProgress sx={{ mb: 2 }} />}

      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="From (Older Sync Date)"
              value={selectedDate1}
              onChange={(e) => setSelectedDate1(e.target.value)}
            >
              <MenuItem value="">Select sync date...</MenuItem>
              {syncDates.map((d) => (
                <MenuItem
                  key={d.date}
                  value={d.date}
                  disabled={d.date === selectedDate2}
                >
                  {d.label}
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({d.saved_count} records, {d.sync_count} sync{d.sync_count === 1 ? '' : 's'})
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
            <TrendingUpIcon sx={{ fontSize: 36, color: '#4680FF' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="To (Newer Sync Date)"
              value={selectedDate2}
              onChange={(e) => setSelectedDate2(e.target.value)}
            >
              <MenuItem value="">Select sync date...</MenuItem>
              {syncDates.map((d) => (
                <MenuItem
                  key={d.date}
                  value={d.date}
                  disabled={d.date === selectedDate1}
                >
                  {d.label}
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({d.saved_count} records, {d.sync_count} sync{d.sync_count === 1 ? '' : 's'})
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Coordinator"
              value={coordinatorFilter}
              onChange={(e) => { setCoordinatorFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {coordinatorOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name || opt.id}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Supervisor"
              value={supervisorFilter}
              onChange={(e) => { setSupervisorFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {supervisorOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name || opt.id}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={schoolOptions}
              value={schoolFilter || null}
              inputValue={schoolSearchText}
              loading={schoolOptionsLoading}
              onChange={(event, value) => {
                const selectedValue = value || '';
                setSchoolFilter(selectedValue);
                setSchoolSearchText(selectedValue);
                setPage(0);
              }}
              onInputChange={(event, value, reason) => {
                setSchoolSearchText(value || '');
                if (reason === 'clear') {
                  setSchoolFilter('');
                  setPage(0);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter Institute"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSchoolFilter(schoolSearchText);
                      setPage(0);
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {schoolOptionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        {selectedDate1 && selectedDate2 && selectedDate1 === selectedDate2 && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Please select two different sync dates.
          </Typography>
        )}
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {compareData && (
        <>
          <Grid container spacing={2} sx={{ mb: 3}}>
            <Grid item xs={12} md={3}>
              <Card elevation={0} sx={{ border: '1px solid #4680FF', borderRadius: 3, bgcolor: '#f0f4ff'}}>
                <CardContent>
                  <Typography variant='overline' color="text.secondary" fontWeight={700}>
                    Total Users
                  </Typography>
                  <Typography variant='h4' fontWeight={800} color="#4680FF">
                    {compareData.total_users || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Module-wise Never Used vs Using Summary
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(summary).map(([key, data]) => {
              const neverUsedChange = data.never_used_change || 0;
              const usingChange = data.using_change || 0;
              const improved = neverUsedChange > 0 || usingChange > 0;
              const worsened = neverUsedChange < 0 || usingChange < 0;
              return (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: improved ? '#16a34a33' : worsened ? '#dc262633' : '#e5e7eb',
                      borderRadius: 3,
                      bgcolor: improved ? '#f0fdf4' : worsened ? '#fef2f2' : '#fff',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {data.label}
                      </Typography>
                      <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                          <Typography variant="caption" color="error.main" fontWeight={700}>
                            Never Used
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {data.never_used_tab1}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">→</Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {data.never_used_tab2}
                          </Typography>
                          {neverUsedChange !== 0 && (
                            <Chip
                              icon={neverUsedChange > 0 ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                              label={`${Math.abs(neverUsedChange)}`}
                              size="small"
                              color={neverUsedChange > 0 ? 'success' : 'error'}
                              variant="outlined"
                              sx={{ fontWeight: 700 }}
                            />
                          )}
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                          <Typography variant="caption" color="success.main" fontWeight={700}>
                            Using
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {data.using_tab1}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">→</Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {data.using_tab2}
                          </Typography>
                          {usingChange !== 0 && (
                            <Chip
                              icon={usingChange > 0 ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                              label={`${Math.abs(usingChange)}`}
                              size="small"
                              color={usingChange > 0 ? 'success' : 'error'}
                              variant="outlined"
                              sx={{ fontWeight: 700 }}
                            />
                          )}
                        </Stack>
                        {neverUsedChange === 0 && usingChange === 0 && (
                          <Chip
                            icon={<RemoveIcon fontSize="small" />}
                            label="No change"
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, alignSelf: 'flex-start' }}
                          />
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Typography variant="body2">
                <strong>Total Institutes:</strong> {compareData.total_institutes}
              </Typography>
              <Typography variant="body2">
                <strong>From:</strong> {date1Label}
              </Typography>
              <Typography variant="body2">
                <strong>To:</strong> {date2Label}
              </Typography>
              {schoolFilter && (
                <Typography variant="body2">
                  <strong>Institute:</strong> {schoolFilter}
                </Typography>
              )}
              {coordinatorFilter && (
                <Typography variant="body2">
                  <strong>Coordinator:</strong> {coordinatorOptions.find(o => o.id === coordinatorFilter)?.name || coordinatorFilter}
                </Typography>
              )}
              {supervisorFilter && (
                <Typography variant="body2">
                  <strong>Supervisor:</strong> {supervisorOptions.find(o => o.id === supervisorFilter)?.name || supervisorFilter}
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper
            ref={tableSectionRef}
            elevation={1}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              // Keep table + pagination in viewport so limit changes don't push data off-screen
              maxHeight: 'calc(100vh - 120px)',
            }}
          >
            <TableContainer ref={tableContainerRef} sx={{ flex: 1, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 200 }}>Institute</TableCell>
                    {Object.values(summary).map((s) => (
                      <TableCell key={s.label} sx={{ fontWeight: 700, bgcolor: '#f8fafc', textAlign: 'center', minWidth: 120 }}>
                        {s.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={Object.keys(summary).length + 1} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={40} thickness={4} color="primary" />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          Loading comparison data...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : sortedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Object.keys(summary).length + 1}>
                        <Typography align="center" sx={{ py: 4 }}>No data to compare.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRows.slice(page * pageSize, page * pageSize + pageSize).map((row) => {
                      const progressStatus = getOverallProgress(row.modules);
                      return (
                        <TableRow key={row.institute_name} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Tooltip title={`Overall Progress: ${progressStatus === 'up' ? 'Improved' : progressStatus === 'down' ? 'Degraded' : 'No Change'}`}>
                                <span>
                              {progressStatus === 'up' && <ArrowUpwardIcon fontSize="small" color="success" />}
                              {progressStatus === 'down' && <ArrowDownwardIcon fontSize="small" color="error" />}
                              {progressStatus === 'neutral' && <TrendingFlatIcon fontSize="small" color="disabled" />}
                            </span>
                              </Tooltip>
                              <span>{row.institute_name}</span>
                            </Stack>
                          </TableCell>
                          {row.modules.map((mod) => (
                          <TableCell key={mod.key} align="center">
                            <Stack alignItems="center" spacing={0.25}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: usageColor(mod.status1),
                                  textDecoration: mod.changed ? 'line-through' : 'none',
                                  opacity: mod.changed ? 0.6 : 1,
                                  fontWeight: 600,
                                }}
                              >
                                {formatUsageLabel(mod.status1)}
                              </Typography>
                              {mod.changed && (
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{
                                    color: mod.improved ? 'success.main' : 'warning.main',
                                  }}
                                >
                                  {formatUsageLabel(mod.status2)}
                                </Typography>
                              )}
                              {!mod.changed && mod.status1 === 'unknown' && (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </Stack>
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  }))}
                </TableBody>
              </Table>
            </TableContainer>
            {sortedRows.length > 0 && (
              <TablePagination
                component="div"
                count={sortedRows.length}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setPageSize(parseInt(event.target.value, 10));
                  setPage(0);
                  // Keep data on screen: jump to table top and reset inner scroll
                  requestAnimationFrame(() => {
                    tableSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    if (tableContainerRef.current) {
                      tableContainerRef.current.scrollTop = 0;
                    }
                  });
                }}
                sx={{ flexShrink: 0, borderTop: '1px solid #e5e7eb' }}
              />
            )}
          </Paper>
        </>
      )}

      {!compareData && !loading && selectedDate1 && selectedDate2 && selectedDate1 !== selectedDate2 && (
        <Typography align="center" sx={{ py: 4 }} color="text.secondary">
          No comparison data available.
        </Typography>
      )}

      {(!selectedDate1 || !selectedDate2) && !datesLoading && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
          <CompareArrowsIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            Select two sync dates above to compare progress.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default SyncProgressReport;
